import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { RosterServicePage } from "@/components/site/RosterServicePage";
import heroImg from "@/assets/impulsadoras.jpg";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/impulsadoras-promotoras";
import { seo } from "@/content/pages/impulsadoras-promotoras.seo";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";
import { getPromotorasGalleryPublic } from "@/server/site/promotoras-gallery.fns";
import { EVENTOS_PROMOTORAS } from "@/content/eventos-realizados";

// Des-canibalización (spec 104, FR-012): esta página vuelve a enfocarse en
// promotoras/impulsadoras; la keyword "anfitrionas" es territorio de
// /anfitrionas y aquí solo se menciona para remitir a esa página.
const DEFAULT_TITLE = seo.title;
const DEFAULT_DESCRIPTION = seo.description;

const props = {
  slug: "/impulsadoras-promotoras" as const,
  galleryTitle: "Nuestro equipo de impulsadoras y promotoras en Lima",
  eventos: {
    titulo: "Promotoras en campañas realizadas",
    intro:
      "Fotos de trabajos reales de nuestras promotoras para marcas, en punto de venta, eventos y ventas en vivo.",
    items: EVENTOS_PROMOTORAS,
  },
};

export const Route = createFileRoute("/impulsadoras-promotoras/")({
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: async ({ context }) => ({
    gallery: await getPromotorasGalleryPublic({ data: { pagina: "impulsadoras" } }),
    head: datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  }),
  component: ImpulsadorasPromotorasPage,
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const heroPhoto = loaderData?.gallery?.[0]?.url;
    // "servicio-impulsadoras-promotoras" (specs/111-slots-imagenes-sitio) solo
    // alimenta el meta OG/JSON-LD acá, igual que en /anfitrionas.
    const slot = match.context.siteImageSlots?.["servicio-impulsadoras-promotoras"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/impulsadoras-promotoras",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/impulsadoras-promotoras"),
        ...(heroPhoto
          ? [
              // Preconnect al origen de Supabase Storage: solo acá y en
              // /anfitrionas, las únicas rutas que sirven fotos reales de la
              // galería directo desde ese storage (PageSpeed marcaba este
              // preconnect como "no utilizado" cuando vivía global en
              // __root.tsx, porque el resto del sitio no lo pide).
              { rel: "preconnect", href: new URL(heroPhoto).origin },
              { rel: "preload", as: "image", href: heroPhoto, fetchPriority: "high" as const },
            ]
          : []),
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData.head,
            "/impulsadoras-promotoras",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});

function ImpulsadorasPromotorasPage() {
  const { gallery } = Route.useLoaderData();
  const { pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  return (
    <PageShell>
      <RosterServicePage {...props} vista={vista} gallery={gallery} />
    </PageShell>
  );
}
